import React, { useState, useEffect } from "react";
import { Col, FormGroup, Input, Label, Row, Card } from "reactstrap";
import { LabelWithToolTip } from "../../../atoms/LabelWithToolTip";
import Select from "react-select";

const IbanForm = (props) => {
  // get the current currency and always have a valid currency shown in the "This is the only [currency] account to keep" label
  const getCurrentCurrency = () => {
    if (props.currency && props.sortedCurrencyList?.find(c => c.name === props.currency)) {
      return props.currency;
    }
    return props.sortedCurrencyList?.[0]?.name || '';
  };

  const [selectedCurrencyName, setSelectedCurrencyName] = useState(getCurrentCurrency());

  useEffect(() => {
    setSelectedCurrencyName(getCurrentCurrency());
  }, [props.currency, props.sortedCurrencyList]);

  const handleCurrencyChange = (selected) => {
    const id = selected?.value ?? "";
    const match = props.sortedCurrencyList?.find(c => c.id == id);
    setSelectedCurrencyName(match?.name || '');
    props.setValue("currencyId", id);
    props.onCurrencyChanged({ target: { value: id } });
  };

  const field = (label, value) => (
    <div className="vbdm__field">
      <div className="vbdm__field-label">{label}</div>
      <div className={`vbdm__field-value${!value ? ' vbdm__field-value--empty' : ''}`}>
        {value || '\u2014'}
      </div>
    </div>
  );

  const corrBicField = props.register("correspondentBic", { required: false });

  return (
    <>
      {/* Currency and Keep This Only fields */}
      <Row>
        <Col sm="12" md="6">
          <FormGroup>
            <LabelWithToolTip
              id="labelCurrency"
              labelText="Currency *"
              tooltipText="Select the currency for this bank account"
            />
            {(() => {
              const opts = (props.sortedCurrencyList || []).map(o => ({ value: o.id, label: o.name }));
              const currentId = props.sortedCurrencyList?.find(c => c.name === props.currency)?.id;
              return (
                <>
                  <input type="hidden" {...props.register("currencyId", { required: true })} />
                  <Select
                    options={opts}
                    value={opts.find(o => String(o.value) === String(currentId)) || null}
                    onChange={handleCurrencyChange}
                    placeholder="Search currency…"
                    isClearable={false}
                  />
                </>
              );
            })()}
          </FormGroup>
          <div className="keep-only-toggle">
            <label className="keep-only-toggle__label" htmlFor="keepThisOnly">
              This is the only <strong>{selectedCurrencyName}</strong> account to keep
            </label>
            <label className="keep-only-toggle__switch" htmlFor="keepThisOnly">
              <input
                type="checkbox"
                id="keepThisOnly"
                name="keepThisOnly"
                {...props.register("keepThisOnly")}
                checked={props.keepThisOnly}
                onChange={props.onKeepThisOnlyChange}
                className="keep-only-toggle__input"
              />
              <span className="keep-only-toggle__track" />
            </label>
          </div>
        </Col>
      </Row>


      <Row>
        <Col sm="12" md="12">
          <FormGroup>
            <LabelWithToolTip
              id="labelBeneficiaryName"
              labelText="Beneficiary Name *"
              tooltipText="Name of the account holder as it appears on the bank account. 
              Only alphanumeric characters, spaces, parentheses, commas and periods. Ampersands (&) are not permitted."
            />
            <input
              {...props.register("beneficiaryBankAccountName", { required: true })}
              maxLength="100"
              type="text"
              className="form-control"
              value={props.beneficiaryName || ""}
              onChange={props.onBeneficiaryNameChange}
            />
          </FormGroup>
        </Col>
      </Row>

      {/* IBAN Account Number */}
      <Row>
        <Col sm="12" md="12">
          <FormGroup>
            <LabelWithToolTip
              id="labelIbanAccountNumber"
              labelText="IBAN Account Number *"
              tooltipText="International Bank Account Number in valid IBAN format (e.g., GB33BUKB20201555555555). 
              Length varies by country (15-34 characters). Only alphanumeric characters allowed."
            />
            <input
              type="text"
              {...props.register("bankAccountNumber", {required: true })}
              className="form-control"
              onChange={props.resetMetadata}
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Correspondent BIC SWIFT Field */}
      <Row>
        <Col sm="12">
          <FormGroup>
            <LabelWithToolTip
              id="labelCorrespondentBic"
              labelText="Correspondent Bank SWIFT BIC"
              tooltipText="Intermediary bank SWIFT code if payments need to be routed through another bank (optional). 
              Must be 8 or 11 characters if provided. Only alphanumeric characters allowed."
            />
            <input
              type="text"
              {...corrBicField}
              onChange={(e) => {
                corrBicField.onChange(e);
                if (props.resetMetadata) props.resetMetadata();
              }}
              className="form-control"
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Bank details - shown after successful IBAN validation */}
      {props.isIbanValidated && (
        <div className="swift-results">
          <div className="swift-results__section">
            <div className="swift-results__fields">
              <input type="hidden" {...props.register("bic")} />
              <input type="hidden" {...props.register("bankName")} />
              <input type="hidden" {...props.register("branch")} />
              <input type="hidden" {...props.register("bankAddress")} />
              <input type="hidden" {...props.register("bankCity")} />
              <input type="hidden" {...props.register("bankZip")} />
              <input type="hidden" {...props.register("isoCountryCode")} />
              <input type="hidden" {...props.register("bankCountry")} />
              <div className="vbdm__grid">
                {field("IBAN", props.getValues("bankAccountNumber"))}
                {field("Beneficiary Bank Name", props.getValues("bankName"))}
                {field("Beneficiary Bank Address", props.getValues("bankAddress"))}
                {field("Beneficiary Bank City", props.getValues("bankCity"))}
                {field("Beneficiary Bank Postal Code", props.getValues("bankZip"))}
                {field("Beneficiary Bank Country Name", props.getValues("bankCountry"))}
                {field("Beneficiary Bank Country Code", props.isoCountryCode)}
              </div>
            </div>
          </div>

          {/* Correspondent Bank Section - shown only when a correspondent BIC was provided and validated */}
          {props.isCorrespondentBicValidated && props.correspondentBicData && (
            <>
            <div className="vbdm__divider" />
            <div className="swift-results__section swift-results__section--corresponding">
              <div className="swift-results__fields">
                <div className="vbdm__grid">
                  {field("Correspondent Bank SWIFT", props.validatedCorrespondentBicCode)}
                  {field("Correspondent Bank Name", props.correspondentBicData.institutionName)}
                  {field("Correspondent Bank Address", props.correspondentBicData.opAddress)}
                  {field("Correspondent Bank City", props.correspondentBicData.opCity)}
                  {field("Correspondent Bank Postal Code", props.correspondentBicData.opZipCode)}
                  {field("Correspondent Bank Country Name", props.correspondentBicData.countryName)}
                  {field("Correspondent Bank Country Code", props.correspondentBicData.countryIso || props.correspondentBicData.isoCountryCode || props.correspondentBicData.countryCode)}
                </div>
              </div>
            </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default IbanForm;
